/**
 * DevWallets — browser-local Keypairs that exist only to drive the gameplay
 * loop from a single tab. Spawn a wallet, airdrop SOL via the faucet, then
 * call kothTouch (or any other ix you bolt on here) signed by that keypair
 * directly. No Phantom round-trip, no extension juggling.
 *
 * The "Touch as …" button is wired against the round id tracked by the URL
 * (`?round=<id>`) / `KOTH_ROUND_LS_KEY` — the same source of truth ChainTab
 * uses, so an init_round done in ChainTab immediately makes these buttons
 * live and vice versa.
 *
 * Safety:
 *   - mainnet endpoints reject airdrops at the RPC layer; we don't gate the
 *     button here so the failure stays visible in the row's `output`.
 *   - secret keys live in plain localStorage. DEV-only by construction
 *     (`useDebugVisible` gate on the parent panel).
 */
import { useConnection } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL, PublicKey, SystemProgram } from "@solana/web3.js";
import { BN } from "@coral-xyz/anchor";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  deriveLastTouchPda,
  deriveRoundPda,
} from "../../games/last-touch/anchor";
import { KOTH_ROUND_LS_KEY } from "../../games/last-touch/useKothLobbyController";
import { explorerTxUrl } from "../explorerLink";
import { pushDevError } from "../store/devErrorLog";
import {
  buildKeypairProgram,
  keypairFromRecord,
  publicKeyFromRecord,
  useDevWallets,
  type DevWalletRecord,
} from "../store/devWallets";
import {
  btnStyle,
  colors,
  copyToClipboard,
  fmtSol,
  shortKey,
  styles,
} from "./_shared";

interface RowStatus {
  kind: "idle" | "running" | "ok" | "fail";
  message?: string;
  sig?: string;
}

export function DevWalletsSection(): JSX.Element {
  const { connection } = useConnection();
  const wallets = useDevWallets((s) => s.wallets);
  const activeId = useDevWallets((s) => s.activeId);
  const spawn = useDevWallets((s) => s.spawn);
  const remove = useDevWallets((s) => s.remove);
  const rename = useDevWallets((s) => s.rename);
  const setActive = useDevWallets((s) => s.setActive);
  const setBalance = useDevWallets((s) => s.setBalance);
  const importSecret = useDevWallets((s) => s.importSecret);

  const [searchParams] = useSearchParams();
  const roundId = useMemo<BN | null>(() => {
    const raw =
      searchParams.get("round") ??
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(KOTH_ROUND_LS_KEY)
        : null);
    if (!raw) return null;
    try {
      return new BN(raw, 10);
    } catch {
      return null;
    }
  }, [searchParams]);
  const roundPda = useMemo<PublicKey | null>(() => {
    if (!roundId) return null;
    try {
      return deriveRoundPda(roundId);
    } catch {
      return null;
    }
  }, [roundId]);

  const [spawnLabel, setSpawnLabel] = useState("");
  const [airdropSol, setAirdropSol] = useState("2");
  const [importJson, setImportJson] = useState("");
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});
  const [bulkBusy, setBulkBusy] = useState(false);

  // Synchronous in-flight guard. React-state `disabled` updates a tick late so
  // a fast double-click can fire both onClicks before the second render lands —
  // and identical back-to-back ix calls collapse to one signature in the
  // cluster, throwing "Transaction has already been processed". Keys are
  // `"airdrop:<id>"` / `"touch:<id>"`.
  const inFlight = useRef<Set<string>>(new Set());

  const updateStatus = useCallback((id: string, s: RowStatus) => {
    setRowStatus((prev) => ({ ...prev, [id]: s }));
  }, []);

  // 8s background poll to keep balances fresh across all dev wallets.
  useEffect(() => {
    if (wallets.length === 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      for (const w of wallets) {
        try {
          const lp = await connection.getBalance(publicKeyFromRecord(w));
          if (cancelled) return;
          setBalance(w.id, lp);
        } catch {
          if (cancelled) return;
          setBalance(w.id, null);
        }
      }
      if (!cancelled) timer = setTimeout(tick, 8_000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Re-arm only when the set of wallets *changes* — not on every balance
    // mutation, which would otherwise restart the loop forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, wallets.length]);

  const requestAirdrop = useCallback(
    async (rec: DevWalletRecord) => {
      const key = `airdrop:${rec.id}`;
      if (inFlight.current.has(key)) return;
      const sol = Number(airdropSol);
      if (!Number.isFinite(sol) || sol <= 0) {
        updateStatus(rec.id, { kind: "fail", message: "invalid SOL amount" });
        return;
      }
      inFlight.current.add(key);
      updateStatus(rec.id, { kind: "running" });
      try {
        const lamports = Math.floor(sol * LAMPORTS_PER_SOL);
        const sig = await connection.requestAirdrop(
          publicKeyFromRecord(rec),
          lamports,
        );
        const latest = await connection.getLatestBlockhash();
        await connection.confirmTransaction(
          {
            signature: sig,
            blockhash: latest.blockhash,
            lastValidBlockHeight: latest.lastValidBlockHeight,
          },
          "confirmed",
        );
        const lp = await connection.getBalance(publicKeyFromRecord(rec));
        setBalance(rec.id, lp);
        updateStatus(rec.id, { kind: "ok", message: `airdropped ${sol} SOL`, sig });
      } catch (e) {
        const msg = (e as Error).message ?? String(e);
        updateStatus(rec.id, { kind: "fail", message: msg });
        pushDevError("devWallets.airdrop", e);
      } finally {
        inFlight.current.delete(key);
      }
    },
    [connection, airdropSol, setBalance, updateStatus],
  );

  const touchAs = useCallback(
    async (rec: DevWalletRecord) => {
      const key = `touch:${rec.id}`;
      if (inFlight.current.has(key)) return;
      if (!roundId || !roundPda) {
        updateStatus(rec.id, {
          kind: "fail",
          message: "no active round (set ?round=… or init_round first)",
        });
        return;
      }
      inFlight.current.add(key);
      updateStatus(rec.id, { kind: "running" });
      try {
        const kp = keypairFromRecord(rec);
        const program = buildKeypairProgram(connection, kp);
        const lastTouch = deriveLastTouchPda(roundPda, kp.publicKey);
        const sig = await (
          program.methods as unknown as {
            kothTouch(): {
              accounts(a: Record<string, unknown>): { rpc(): Promise<string> };
            };
          }
        )
          .kothTouch()
          .accounts({
            round: roundPda,
            lastTouch,
            toucher: kp.publicKey,
            systemProgram: SystemProgram.programId,
          })
          .rpc();
        const lp = await connection.getBalance(kp.publicKey);
        setBalance(rec.id, lp);
        updateStatus(rec.id, { kind: "ok", message: "touched", sig });
      } catch (e) {
        const msg = (e as Error).message ?? String(e);
        updateStatus(rec.id, { kind: "fail", message: msg });
        pushDevError("devWallets.touchAs", e);
      } finally {
        inFlight.current.delete(key);
      }
    },
    [connection, roundId, roundPda, setBalance, updateStatus],
  );

  const spawnAndAirdrop = useCallback(async () => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const rec = spawn(spawnLabel || undefined);
      setSpawnLabel("");
      await requestAirdrop(rec);
    } finally {
      setBulkBusy(false);
    }
  }, [bulkBusy, requestAirdrop, spawn, spawnLabel]);

  const spawn4AndAirdrop = useCallback(async () => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const labels = ["alpha", "bravo", "charlie", "delta"];
      for (const lbl of labels) {
        const existing = wallets.find((w) => w.label === lbl);
        const rec = existing ?? spawn(lbl);
        // Sequential, not parallel — faucets rate-limit per IP and parallel
        // requestAirdrop calls usually collapse into one or fail.
        await requestAirdrop(rec);
      }
    } finally {
      setBulkBusy(false);
    }
  }, [bulkBusy, requestAirdrop, spawn, wallets]);

  // End-to-end gameplay smoke test. Spawn-or-reuse alpha/bravo/charlie/delta,
  // airdrop each, then touch the active round as each in sequence. Inter-touch
  // sleep is required: identical-shape txs at the same blockhash collapse to a
  // single signature and the cluster rejects the dup as "already processed".
  const runSmokeTest = useCallback(async () => {
    if (bulkBusy) return;
    if (!roundId || !roundPda) {
      updateStatus("__smoke", {
        kind: "fail",
        message: "no active round — init_round in Chain tab first, or set ?round=…",
      });
      return;
    }
    setBulkBusy(true);
    updateStatus("__smoke", { kind: "running", message: "starting…" });
    try {
      const labels = ["alpha", "bravo", "charlie", "delta"];

      // Phase 1 — ensure wallets exist + funded.
      updateStatus("__smoke", { kind: "running", message: "spawning + airdropping 4 wallets…" });
      const recs: DevWalletRecord[] = [];
      for (const lbl of labels) {
        const existing = wallets.find((w) => w.label === lbl);
        const rec = existing ?? spawn(lbl);
        recs.push(rec);
        const lp = await connection.getBalance(publicKeyFromRecord(rec));
        if (lp < 0.5 * LAMPORTS_PER_SOL) {
          await requestAirdrop(rec);
        }
      }

      // Phase 2 — touch round as each. Sleep between to defeat tx dedup; the
      // round_touch ix also has a cooldown gate so the gap doubles as a real
      // cooldown probe.
      for (let i = 0; i < recs.length; i++) {
        const rec = recs[i]!;
        updateStatus("__smoke", {
          kind: "running",
          message: `touch ${i + 1}/${recs.length} — ${rec.label}…`,
        });
        await touchAs(rec);
        if (i < recs.length - 1) {
          await new Promise((r) => setTimeout(r, 2000));
        }
      }

      const lastLabel = recs[recs.length - 1]?.label ?? "?";
      updateStatus("__smoke", {
        kind: "ok",
        message: `4-wallet touch loop ok — last toucher: ${lastLabel}`,
      });
    } catch (e) {
      const msg = (e as Error).message ?? String(e);
      updateStatus("__smoke", { kind: "fail", message: msg });
      pushDevError("devWallets.smokeTest", e);
    } finally {
      setBulkBusy(false);
    }
  }, [
    bulkBusy,
    connection,
    requestAirdrop,
    roundId,
    roundPda,
    spawn,
    touchAs,
    updateStatus,
    wallets,
  ]);

  const handleImport = useCallback(() => {
    try {
      const parsed = JSON.parse(importJson.trim());
      if (!Array.isArray(parsed)) throw new Error("expected number[]");
      const bytes = (parsed as number[]).map((n) => Number(n));
      const rec = importSecret(bytes);
      setImportJson("");
      updateStatus(rec.id, { kind: "ok", message: "imported" });
    } catch (e) {
      const msg = (e as Error).message ?? String(e);
      updateStatus("__import", { kind: "fail", message: msg });
    }
  }, [importJson, importSecret, updateStatus]);

  const importStatus = rowStatus.__import;
  const smokeStatus = rowStatus.__smoke;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>Dev Wallets</div>
          <div style={styles.sectionSubtitle}>
            Browser-local keypairs · airdrop + sign KotH ix directly · DEV ONLY
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={() => void spawn4AndAirdrop()}
            style={btnStyle({ tone: "primary", disabled: bulkBusy })}
            disabled={bulkBusy}
          >
            {bulkBusy ? "Working…" : "Spawn 4 + airdrop"}
          </button>
          <button
            onClick={() => void runSmokeTest()}
            style={btnStyle({ tone: "primary", disabled: bulkBusy || !roundId })}
            disabled={bulkBusy || !roundId}
            title={
              roundId
                ? "Spawn/reuse 4 wallets, airdrop, then touch the round as each in sequence"
                : "Set ?round= or init_round first"
            }
          >
            {bulkBusy ? "Working…" : "Run smoke test"}
          </button>
        </div>
      </div>
      {smokeStatus && smokeStatus.kind !== "idle" && (
        <div style={{ padding: "0 12px 8px 12px" }}>
          <span style={statusPillStyle(smokeStatus.kind)}>
            SMOKE {smokeStatus.kind.toUpperCase()}
            {smokeStatus.message ? ` — ${smokeStatus.message}` : ""}
          </span>
        </div>
      )}

      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>Spawn</div>
          <div style={styles.row}>
            <input
              value={spawnLabel}
              onChange={(e) => setSpawnLabel(e.target.value)}
              placeholder="label (optional, e.g. alice)"
              style={styles.inputStyle}
            />
            <input
              value={airdropSol}
              onChange={(e) => setAirdropSol(e.target.value)}
              placeholder="SOL"
              style={{ ...styles.inputStyle, flex: "0 0 80px" }}
            />
            <button
              onClick={() => void spawnAndAirdrop()}
              style={btnStyle({ tone: "primary", disabled: bulkBusy })}
              disabled={bulkBusy}
            >
              {bulkBusy ? "Working…" : "Spawn + airdrop"}
            </button>
            <button onClick={() => spawn(spawnLabel || undefined)} style={btnStyle()}>
              Spawn only
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>Import secret key</div>
          <div style={styles.row}>
            <input
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder="[64-byte secretKey JSON array, e.g. from tools/.koth-keys/alice.json]"
              style={styles.inputStyle}
            />
            <button onClick={handleImport} style={btnStyle()} disabled={!importJson.trim()}>
              Import
            </button>
          </div>
          {importStatus?.kind === "fail" && (
            <div style={{ ...styles.mono, color: colors.fail }}>{importStatus.message}</div>
          )}
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            Wallets ({wallets.length})
            {roundId && (
              <span style={{ color: colors.textFaint, fontWeight: 500, marginLeft: 8 }}>
                · touching round #{roundId.toString()}
              </span>
            )}
          </div>
          {wallets.length === 0 ? (
            <div style={{ ...styles.mono, color: colors.textMute }}>(no wallets yet)</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {wallets.map((w) => (
                <WalletRow
                  key={w.id}
                  rec={w}
                  active={w.id === activeId}
                  status={rowStatus[w.id]}
                  hasRound={roundId !== null}
                  onAirdrop={() => void requestAirdrop(w)}
                  onTouch={() => void touchAs(w)}
                  onSetActive={() => setActive(w.id)}
                  onRename={(label) => rename(w.id, label)}
                  onRemove={() => remove(w.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WalletRow({
  rec,
  active,
  status,
  hasRound,
  onAirdrop,
  onTouch,
  onSetActive,
  onRename,
  onRemove,
}: {
  rec: DevWalletRecord;
  active: boolean;
  status?: RowStatus;
  hasRound: boolean;
  onAirdrop: () => void;
  onTouch: () => void;
  onSetActive: () => void;
  onRename: (label: string) => void;
  onRemove: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [labelDraft, setLabelDraft] = useState(rec.label);
  useEffect(() => setLabelDraft(rec.label), [rec.label]);

  return (
    <div style={walletRowStyle(active)}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {editing ? (
          <input
            value={labelDraft}
            autoFocus
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={() => {
              setEditing(false);
              if (labelDraft.trim() && labelDraft !== rec.label) onRename(labelDraft.trim());
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              if (e.key === "Escape") {
                setLabelDraft(rec.label);
                setEditing(false);
              }
            }}
            style={{ ...styles.inputStyle, flex: "0 0 120px" }}
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            style={{
              ...btnStyle({ tone: "ghost", small: true }),
              fontWeight: 700,
              flex: "0 0 auto",
            }}
            title="Rename"
          >
            {rec.label}
          </button>
        )}
        {active && (
          <span style={activeBadgeStyle}>ACTIVE</span>
        )}
        <code
          style={{ ...styles.mono, fontSize: 10 }}
          title={rec.publicKey}
        >
          {shortKey(rec.publicKey)}
        </code>
        <button
          onClick={() => void copyToClipboard(rec.publicKey)}
          style={btnStyle({ tone: "ghost", small: true })}
        >
          Copy pk
        </button>
        <button
          onClick={() => void copyToClipboard(JSON.stringify(rec.secretKey))}
          style={btnStyle({ tone: "ghost", small: true })}
          title="Secret key — DEV ONLY"
        >
          Copy sk
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ ...styles.mono, color: colors.mono }}>
          {fmtSol(rec.balanceLamports)} SOL
        </span>
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
        <button
          onClick={onAirdrop}
          style={btnStyle({ tone: "primary", small: true, disabled: status?.kind === "running" })}
          disabled={status?.kind === "running"}
        >
          Airdrop
        </button>
        <button
          onClick={onTouch}
          style={btnStyle({ small: true, disabled: !hasRound || status?.kind === "running" })}
          disabled={!hasRound || status?.kind === "running"}
          title={hasRound ? "Sign kothTouch with this wallet" : "Set ?round= first"}
        >
          Touch as this
        </button>
        {!active && (
          <button onClick={onSetActive} style={btnStyle({ tone: "ghost", small: true })}>
            Set active
          </button>
        )}
        <button
          onClick={onRemove}
          style={btnStyle({ tone: "danger", small: true })}
          title="Remove from store (does not return funds)"
        >
          Remove
        </button>
        {status && status.kind !== "idle" && (
          <span style={statusPillStyle(status.kind)}>
            {status.kind.toUpperCase()}
            {status.message ? ` — ${status.message.slice(0, 80)}` : ""}
          </span>
        )}
        {status?.sig && (
          <a
            href={explorerTxUrl(status.sig)}
            target="_blank"
            rel="noreferrer"
            style={{ ...styles.mono, color: colors.running, fontSize: 10 }}
          >
            tx: {status.sig.slice(0, 6)}…{status.sig.slice(-4)}
          </a>
        )}
      </div>
    </div>
  );
}

function walletRowStyle(active: boolean): React.CSSProperties {
  return {
    padding: "8px 10px",
    borderTop: `1px solid ${colors.border}`,
    background: active ? "rgba(74,142,255,0.06)" : "transparent",
  };
}

const activeBadgeStyle: React.CSSProperties = {
  padding: "1px 6px",
  background: "#1c2433",
  color: "#7ab0ff",
  border: "1px solid #2a4a7a",
  borderRadius: 3,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.5,
};

function statusPillStyle(kind: RowStatus["kind"]): React.CSSProperties {
  const palette: Record<RowStatus["kind"], { bg: string; color: string }> = {
    idle: { bg: "#22222e", color: "#9aa0b4" },
    running: { bg: "#23344f", color: "#7ab0ff" },
    ok: { bg: "#1c3a26", color: "#7af09e" },
    fail: { bg: "#3a1c26", color: "#f47a8e" },
  };
  const p = palette[kind];
  return {
    padding: "2px 6px",
    background: p.bg,
    color: p.color,
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    maxWidth: 380,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
}
